const THUMBNAIL_WIDTH = 640;
const THUMBNAIL_HEIGHT = 360;

function waitForEvent(target: HTMLMediaElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      target.removeEventListener(event, handleSuccess);
      target.removeEventListener("error", handleError);
    };
    const handleSuccess = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error("This video could not be read. Try exporting it as MP4 or WebM."));
    };

    target.addEventListener(event, handleSuccess, { once: true });
    target.addEventListener("error", handleError, { once: true });
  });
}

export async function createVideoThumbnail(
  file: File,
): Promise<{ thumbnail: Blob; durationSeconds: number }> {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;

  try {
    video.src = objectUrl;
    await waitForEvent(video, "loadedmetadata");
    const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;
    video.currentTime = Math.min(Math.max(durationSeconds * 0.25, 0.1), 3);
    await waitForEvent(video, "seeked");

    const canvas = document.createElement("canvas");
    canvas.width = THUMBNAIL_WIDTH;
    canvas.height = THUMBNAIL_HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Your browser could not create a thumbnail.");

    const sourceRatio = video.videoWidth / video.videoHeight || 16 / 9;
    const targetRatio = THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT;
    let sourceWidth = video.videoWidth;
    let sourceHeight = video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceRatio > targetRatio) {
      sourceWidth = video.videoHeight * targetRatio;
      sourceX = (video.videoWidth - sourceWidth) / 2;
    } else {
      sourceHeight = video.videoWidth / targetRatio;
      sourceY = (video.videoHeight - sourceHeight) / 2;
    }

    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      THUMBNAIL_WIDTH,
      THUMBNAIL_HEIGHT,
    );

    const thumbnail = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Thumbnail creation failed."))),
        "image/jpeg",
        0.82,
      );
    });

    return { thumbnail, durationSeconds };
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(objectUrl);
    video.remove();
  }
}
